/**
 * The Daily Huddle cash view's one type and spacing scale, taken from the mock
 * and the record tile. Every step builds from these classes instead of its own
 * sizes. Values are rendered px: the app's root font is 17.12px, so rem-based
 * Tailwind sizes (`text-sm` = 15px) would drift from the mock.
 */

/** Step header: a gutter with "Step N", then the title and its question. The first beat's
 *  hairline closes it. */
export const STEP_HEADER = "grid gap-x-6 gap-y-1 lg:grid-cols-[64px_1fr]";
export const STEP_GUTTER = "font-mono text-[12px] leading-[18px] text-muted-foreground lg:pt-[9px]";
export const STEP_TITLE = "text-[24px] font-semibold leading-[30px]";
export const STEP_QUESTION = "mt-1 text-[14px] leading-[20px] text-muted-foreground";

/** A step's beats: a hairline above each, 32px between them, no outer card. */
export const BEATS = "grid gap-[32px]";
export const BEAT = "flex min-w-0 flex-col border-t pt-[16px]";
export const BEAT_HEADER = "flex flex-wrap items-baseline gap-x-3 gap-y-1";
export const BEAT_FIGURE = "text-[18px] font-bold leading-[24px] tabular-nums";
export const BEAT_TITLE = "text-[18px] font-semibold leading-[24px]";
export const BEAT_CHIP = "rounded-full border px-2 py-px text-[12px] font-semibold leading-[16px]";
export const BEAT_HINT = "text-[12px] leading-[18px] text-muted-foreground";
/** A beat's empty line ("Nothing here today."). */
export const BEAT_EMPTY = "mt-3 text-[14px] text-muted-foreground";

/** Record tile: the reference unit every list is built from. */
export const TILE = "rounded-[6px] border bg-card p-[12px]";
export const TILE_NAME = "text-[14px] font-semibold leading-[20px]";
/** Tile line and amount are shared with the feed and deal tiles. The cash huddle's scale
 *  (`huddle-scale.module.css`) sets them to 12px and 16px. Everywhere else they stay 13px and 18px. */
export const TILE_LINE = "text-[length:var(--tile-line,13px)] leading-[var(--tile-line-lh,18px)]";
export const TILE_QUIET = "text-[12px] leading-[16px] text-muted-foreground";
export const TILE_AMOUNT = "text-[length:var(--tile-amount,18px)] font-semibold leading-[22px] tabular-nums";
export const TILE_GAP = "gap-[10px]";

/** Count tile: one figure with its label and caption (also the Yesterday metric cards). */
export const COUNT_TILE = "rounded-[8px] border bg-card p-[14px]";
export const COUNT_LABEL = "text-[12px] font-medium leading-[16px] text-muted-foreground";
export const COUNT_FIGURE = "text-[24px] font-bold leading-[30px] tabular-nums";
export const COUNT_CAPTION = "text-[12px] leading-[16px] text-muted-foreground";

/** Commit's equation tile. */
export const EQUATION_TILE = "rounded-[8px] border bg-card p-[12px]";
export const EQUATION_LABEL = "text-[12px] font-medium leading-[16px] text-muted-foreground";
export const EQUATION_FIGURE = "text-[18px] font-bold leading-[24px] tabular-nums";
export const EQUATION_TARGET = "text-[12px] leading-[16px] text-muted-foreground tabular-nums";

/** Plan strip: chip labels 11 and amounts 15/600 stay; the Totals' figure is set here. */
export const PLAN_TOTAL_FIGURE = "text-[18px] font-bold leading-[24px] tabular-nums";

/** One size for every row action (Done, Carry to today, Confirm, Reassign, Undo, ...). */
export const ROW_ACTION = "h-7 gap-1 px-2.5 text-[12px] has-[>svg]:px-2";
/** A small segmented control (Tabs) that sits beside a card label. */
export const SEGMENTED_LIST = "h-8";
export const SEGMENTED_TRIGGER = "px-2.5 text-[12px]";

/** Owner strip chip. */
export const OWNER_CHIP = "inline-flex h-[28px] items-center gap-1.5 rounded-full border bg-card pl-1 pr-2.5 text-[12px]";
export const OWNER_CHIP_AVATAR = "h-[20px] w-[20px] text-[8px]";

/** Count tile's foot: a thin bar toward target with the target beside it (Yesterday's beat 1). */
export const COUNT_BAR = "h-[6px] flex-1 bg-muted";
/** A beat's text link (the "N didn't land" footer). */
export const BEAT_LINK = "inline-flex items-center gap-1 text-[14px] font-semibold leading-[20px] text-primary";

/** Service board: three equal panels under the board beat's header, and the beat's sub-lists. The
 *  panels sit side by side only where each gets a readable width (the step column shares the row with
 *  the commitments sidebar); below that they stack. */
export const BOARD_PANELS = "grid gap-[10px] min-[1600px]:grid-cols-3";
export const BOARD_BAR = "h-[8px] overflow-hidden rounded-full bg-muted";
export const BEAT_SUBHEAD = "text-[14px] font-semibold leading-[20px]";
